import React, { useState } from 'react';
import { Plus, Calendar, AlertCircle, Edit, Trash2, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import ContractEditModal from '@/components/ContractEditModal';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';

export default function Contratos() {
  const { data: contractsData, isLoading: loadingContracts, refresh: refreshContracts } = useApiData('/contracts');
  const { data: clientsData, refresh: refreshClients } = useApiData('/clients');

  const contracts = (contractsData || []).filter(c => c.status !== 'inativo');
  const clients = clientsData || [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    contract_type: '',
    valor_recorrente: 0,
    billing_frequency: 'mensal',
    start_date: '',
    duration_months: 12,
    escopo_resumido: '',
    notes: '',
    auto_asaas_billing: false,
    asaas_billing_type: 'UNDEFINED'
  });
  const [loading, setLoading] = useState(false);

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente inativar este contrato?")) {
      try {
        await api.delete(`/contracts/${id}`);
        toast.success("Contrato inativado com sucesso");
        refreshContracts();
      } catch (error) {
        toast.error("Erro ao inativar contrato");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
      };

      await api.post('/contracts', payload);
      toast.success('Contrato criado com sucesso!');
      setIsDialogOpen(false);
      resetForm();
      refreshContracts();

    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao criar contrato');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      title: '',
      contract_type: '',
      valor_recorrente: 0,
      billing_frequency: 'mensal',
      start_date: '',
      duration_months: 12,
      escopo_resumido: '',
      notes: '',
      auto_asaas_billing: false,
      asaas_billing_type: 'UNDEFINED'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      ativo: 'default',
      pausado: 'secondary',
      encerrado: 'destructive'
    };
    return <Badge variant={variants[status] || 'default'}>{status.toUpperCase()}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente não encontrado';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="contratos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Contratos</h1>
          <p className="text-slate-600 mt-1">Gerencie os contratos dos clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-contract-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Contrato
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Contrato</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  required
                >
                  <SelectTrigger data-testid="select-client">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Título do Contrato *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Gestão de Tráfego 2024"
                  required
                  data-testid="contract-title"
                />
              </div>

              <div>
                <Label>Tipo de Contrato *</Label>
                <Select
                  value={formData.contract_type}
                  onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
                >
                  <SelectTrigger data-testid="select-contract-type">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing_digital">Marketing Digital</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="trafego_pago">Tráfego Pago</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="consultoria">Consultoria</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valor_recorrente">Valor Mensal (R$) *</Label>
                  <Input
                    id="valor_recorrente"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.valor_recorrente}
                    onChange={(e) => setFormData({ ...formData, valor_recorrente: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="contract-value"
                  />
                </div>
                <div>
                  <Label>Frequência</Label>
                  <Select
                    value={formData.billing_frequency}
                    onValueChange={(value) => setFormData({ ...formData, billing_frequency: value })}
                  >
                    <SelectTrigger data-testid="select-billing-freq">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                      <SelectItem value="unico">Único</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Data de Início *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="contract-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="duration">Duração (meses) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) })}
                    required
                    data-testid="contract-duration"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="escopo">Escopo Resumido</Label>
                <textarea
                  id="escopo"
                  className="w-full min-h-[60px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  value={formData.escopo_resumido}
                  onChange={(e) => setFormData({ ...formData, escopo_resumido: e.target.value })}
                  placeholder="Descreva brevemente o escopo do contrato"
                  data-testid="contract-escopo"
                />
              </div>

              <div>
                <Label htmlFor="notes">Observações</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="contract-notes"
                />
              </div>

              {/* === Automação Asaas === */}
              <div className="border border-violet-100 bg-violet-50/40 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-violet-600" />
                    <span className="text-sm font-semibold text-slate-700">Cobranças Automáticas (Asaas)</span>
                  </div>
                  <Switch
                    id="auto_asaas_billing"
                    checked={formData.auto_asaas_billing}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_asaas_billing: checked })}
                  />
                </div>
                {formData.auto_asaas_billing && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600">Forma de Pagamento Padrão</Label>
                    <Select
                      value={formData.asaas_billing_type}
                      onValueChange={(v) => setFormData({ ...formData, asaas_billing_type: v })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Escolher modo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNDEFINED">🔗 Cliente escolhe no link</SelectItem>
                        <SelectItem value="PIX">⚡ Apenas PIX</SelectItem>
                        <SelectItem value="BOLETO">🧳 Apenas Boleto</SelectItem>
                        <SelectItem value="CREDIT_CARD">💳 Cartão de Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      As faturas serão criadas automaticamente no Asaas ao salvar o contrato.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading || !formData.contract_type} data-testid="submit-contract-btn">
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Cliente</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Tipos</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Início</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Fim</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {contracts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-slate-500">
                    Nenhum contrato encontrado
                  </td>
                </tr>
              ) : (
                contracts.map((contract) => (
                  <tr key={contract.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`contract-row-${contract.id}`}>
                    <td className="p-4 align-middle text-sm font-medium text-slate-700">{getClientName(contract.client_id)}</td>
                    <td className="p-4 align-middle text-sm">
                      <div className="flex gap-1 flex-wrap">
                        {contract.contract_type && (
                          <Badge variant="secondary" className="text-xs">{contract.contract_type}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4 align-middle text-sm text-slate-600">{formatDate(contract.start_date)}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{contract.end_date ? formatDate(contract.end_date) : 'Indeterminado'}</td>
                    <td className="p-4 align-middle text-sm">{getStatusBadge(contract.status)}</td>
                    <td className="p-4 align-middle text-sm">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(contract)}
                          data-testid={`edit-contract-${contract.id}`}
                          title="Editar contrato"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(contract.id)}
                          data-testid={`delete-contract-${contract.id}`}
                          title="Inativar contrato"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContractEditModal
        contract={selectedContract}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={refreshContracts}
      />
    </div>
  );
}